// Story 11.4 — /account/wallet/receipts/:receiptId.
//
// The document, rendered from what the server sends and NOTHING ELSE.
//
// ⛔ THE RULE THIS PAGE EXISTS TO KEEP: it computes no tax, formats no rate and
// infers nothing from the amounts. Every VAT-bearing field is ABSENT from the
// payload in the unregistered phase (the server omits the keys rather than
// sending nulls — `domain::topup_receipt::RenderedReceipt`), so a line renders
// here only when the server put a value in it. A page that derived "VAT = 0"
// from a missing field would state a VAT amount while the platform is not
// registered to charge one, which is the exact hazard the story exists for
// (`docs/vat-topup-toggle-and-country-2026-09-01.md` §7).
//
// The plain-text `text` field is the same document the server renders for a
// support paste or an archive; this page lays out the fields so the screen
// version is legible, and the two agree because both come from one render.
//
// ⚑ THE SERIES CARRIES THREE DOCUMENT TYPES, AND THEY SAY OPPOSITE THINGS
// (Story 11.7 + OQ-1). A receipt records money taken, a credit note records a
// refund back to the card, and a debit note records a failed refund put back on
// the wallet balance instead. The page used to render a hardcoded "Total paid"
// under every one of them — telling a buyer their card was refunded when it was
// not is the exact confusion the debit note exists to end. The wording below is
// a MAP OVER `document_type` and nothing more; the amounts, the title and the
// reversal wording all still come from the server's own render.

import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { attachRequestIdToScope } from "../lib/api-client";

type Seller = {
  legal_name: string;
  address: string;
  country: string;
  company_number?: string;
};

type Receipt = {
  receipt_id: string;
  document_number: string;
  /** `receipt` | `credit_note` | `debit_note`. */
  document_type: string;
  title: string;
  issued_on: string;
  seller: Seller;
  /** Registered phase only. */
  seller_vat_registration_number?: string;
  /** Registered phase only, and only when the evidence located a country. */
  buyer_country?: string;
  /**
   * The document this one reverses: the receipt on a credit note, the CREDIT
   * NOTE on a debit note. Absent on a receipt, which reverses nothing.
   */
  credits_document_number?: string;
  description: string;
  currency: string;
  total_cents: number;
  /** Present only when VAT was actually charged. */
  net_cents?: number;
  rate?: string;
  vat_amount_cents?: number;
  /** The registered-phase statement for a supply that carried no VAT. */
  treatment_note?: string;
  /** Debit note only — what happened to the refund that failed. */
  reversal_note?: string;
  text: string;
};

/**
 * The document-type map. ⚠ ONE ENTRY PER TYPE IN THE SERIES, and the words
 * match the server's own plain-text render (`domain::topup_receipt::render_text`)
 * so the screen and the archived copy of one document never disagree.
 *
 * `fallbackTitle` is used only while the document is still loading, where the
 * type is not yet known either — hence the neutral word. Once it arrives, the
 * title is the SERVER's: it also encodes the registration phase ("VAT receipt"
 * versus "Receipt"), which this page must never try to infer.
 */
const DOCUMENT_WORDING: Record<
  string,
  { totalLabel: string; reversesLabel: string }
> = {
  receipt: { totalLabel: "Total paid", reversesLabel: "" },
  credit_note: {
    totalLabel: "Total refunded",
    reversesLabel: "Credit note for receipt",
  },
  debit_note: {
    // ⛔ NOT "refunded". A debit note means the refund to the card FAILED and
    // the money went back to the wallet balance instead.
    totalLabel: "Total credited back",
    reversesLabel: "Reverses credit note",
  },
};

const UNKNOWN_DOCUMENT = { totalLabel: "Total", reversesLabel: "Reverses" };

function wordingFor(documentType: string | undefined) {
  return (documentType && DOCUMENT_WORDING[documentType]) || UNKNOWN_DOCUMENT;
}

function formatMoney(cents: number, currency: string): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${currency} ${Math.floor(abs / 100)}.${(abs % 100)
    .toString()
    .padStart(2, "0")}`;
}

async function fetchReceipt(receiptId: string): Promise<Receipt> {
  const response = await fetch(`/api/v1/wallet/receipts/${receiptId}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) throw new Error(`receipt fetch failed: ${response.status}`);
  return (await response.json()) as Receipt;
}

/** One label/value line. Rendered only when the server sent a value. */
function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
      <dt className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground sm:w-56 sm:shrink-0">
        {label}
      </dt>
      <dd className="font-body text-sm">{value}</dd>
    </div>
  );
}

export function ReceiptPage() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const receipt = useQuery({
    queryKey: ["wallet-receipt", receiptId],
    queryFn: () => fetchReceipt(receiptId as string),
    enabled: Boolean(receiptId),
    staleTime: Infinity,
    retry: false,
  });

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6 text-brand-paper-100">
      <header className="flex flex-col gap-2">
        <Link
          to="/account/wallet"
          className="text-sm underline decoration-dotted opacity-80"
        >
          ← Back to wallet
        </Link>
        {/* ⛔ NOT "Receipt" while loading. A credit note and a debit note come
            through this same route, and a heading that names the wrong document
            for a beat is the same wrong claim, briefly. */}
        <h1 className="font-heading text-4xl font-bold">
          {receipt.data?.title ?? "Document"}
        </h1>
      </header>

      {receipt.isPending && (
        <p className="font-body text-sm text-muted-foreground">
          Loading your receipt…
        </p>
      )}
      {receipt.isError && (
        <p className="font-body text-sm text-destructive" role="alert">
          We couldn&apos;t find that receipt. Open it from Recent activity in
          your wallet, or contact support if it should be there.
        </p>
      )}

      {receipt.data && (
        <article
          data-testid="receipt-document"
          className="flex flex-col gap-5 rounded-lg border bg-card p-6 text-card-foreground"
        >
          <dl className="flex flex-col gap-2">
            <Field label="Document number" value={receipt.data.document_number} />
            {/* The label says which way round the pair is: a credit note
                reverses a RECEIPT, a debit note reverses the CREDIT NOTE. */}
            <Field
              label={wordingFor(receipt.data.document_type).reversesLabel}
              value={receipt.data.credits_document_number}
            />
            <Field label="Date" value={receipt.data.issued_on} />
            <Field label="Seller" value={receipt.data.seller.legal_name} />
            <Field label="Address" value={receipt.data.seller.address} />
            {/* A CVR identifies the company. It is NOT a VAT number and is
                labelled in full so it cannot be read as one. */}
            <Field
              label="Company registration number (CVR)"
              value={receipt.data.seller.company_number}
            />
            {/* Registered phase only — the server omits the key otherwise. */}
            <Field
              label="VAT registration number"
              value={receipt.data.seller_vat_registration_number}
            />
            <Field
              label="Customer's member state"
              value={receipt.data.buyer_country}
            />
          </dl>

          <div className="flex flex-col gap-2 border-t pt-4">
            <div className="flex items-baseline justify-between gap-4">
              <span className="font-body text-sm">
                {receipt.data.description}
              </span>
              <span className="font-mono text-sm tabular-nums">
                {formatMoney(receipt.data.total_cents, receipt.data.currency)}
              </span>
            </div>

            {/* Rendered ONLY when the server sent an amount. Never derived, and
                never shown as a zero. */}
            {receipt.data.net_cents !== undefined && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-body text-sm text-muted-foreground">
                  Net
                </span>
                <span className="font-mono text-sm tabular-nums">
                  {formatMoney(receipt.data.net_cents, receipt.data.currency)}
                </span>
              </div>
            )}
            {receipt.data.vat_amount_cents !== undefined && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-body text-sm text-muted-foreground">
                  VAT {receipt.data.rate}
                </span>
                <span className="font-mono text-sm tabular-nums">
                  {formatMoney(
                    receipt.data.vat_amount_cents,
                    receipt.data.currency,
                  )}
                </span>
              </div>
            )}

            {/* ⚠ The label follows the DOCUMENT TYPE, not the sign. The figure
                stays positive on a reversal — a negative number next to the
                word "refunded" reads as a charge to half the people who see it
                — and the document's own title says which direction it went. */}
            <div className="mt-2 flex items-baseline justify-between gap-4 border-t pt-3">
              <span className="font-body text-sm font-semibold">
                {wordingFor(receipt.data.document_type).totalLabel}
              </span>
              <span className="font-mono text-lg font-bold tabular-nums">
                {formatMoney(receipt.data.total_cents, receipt.data.currency)}
              </span>
            </div>
          </div>

          {/* The cash story before the tax treatment: a buyer reads a debit note
              to find out what happened to their refund, not to read about VAT. */}
          {receipt.data.reversal_note && (
            <p className="font-body text-sm">{receipt.data.reversal_note}</p>
          )}

          {receipt.data.treatment_note && (
            <p className="font-body text-sm text-muted-foreground">
              {receipt.data.treatment_note}
            </p>
          )}
        </article>
      )}
    </main>
  );
}
